<template>
  <div class="main">
    <CustomModal
      v-model="modalMixin.visible"
      :closable="modalMixin.closable"
      :mask-closable="modalMixin.maskClosable"
      :category="modalMixin.category"
      :title="modalMixin.title"
      :message="modalMixin.message"
      :action-buttons="modalMixin.actionButtons"
      :cancel-button="modalMixin.cancelButton"
      :is-form-valid="isFormValid"
      @closeModal="closeActionWindow"
    >
      <template #content>
        <Form
          v-show="modalMixin.showForm"
          ref="loginNoticeForm"
          :model="form"
          :rules="formRules"
          class="form"
        >
          <FormItem prop="description">
            <label>Description:</label>
            <Input
              v-model="form.description"
              name="noticeDescription"
              placeholder="Description"
              clearable
            />
          </FormItem>
          <div class="two-columns">
            <FormItem
              prop="startDate"
              class="column-left"
            >
              <label>Start Date:</label>
              <DatePicker
                v-model="form.startDate"
                placeholder="Start Date"
                type="date"
                format="yyyy-MM-dd"
              />
            </FormItem>
            <FormItem
              prop="endDate"
              class="column-right"
            >
              <label>End Date:</label>
              <DatePicker
                v-model="form.endDate"
                placeholder="End Date"
                type="date"
                format="yyyy-MM-dd"
              />
            </FormItem>
          </div>
        </Form>
      </template>
    </CustomModal>

    <div class="title">
      <h2>{{ title }}</h2>
      <!--TODO: possible to add tooltip? -->
      <AdminToolBar
        show-add
        show-reset
        @onAddItem="openAddLoginNoticeForm"
        @onResetData="onResetNotices"
      />
    </div>
    <div
      ref="content"
      class="content"
    >
      <Spin
        v-if="loading"
        fix
      >
        <Icon
          type="ios-loading"
          size="18"
          class="spin"
        />
        <div>Loading</div>
      </Spin>
      <template v-else>
        <div
          v-if="!list.length"
          class="no-data"
        >
          There isn't any registered Notice.
        </div>
        <Table
          v-else
          :width="contentWidth"
          :max-height="contentHeight"
          :columns="columns"
          :data="list"
          no-data-text="No notices match the selected filter(s)"
          no-filtered-data-text="No notices match the selected filter(s)"
          class="table"
        >
          <template
            slot="description"
            slot-scope="{ row }"
          >
            <p v-dompurify-html="row.description" />
          </template>
          <template
            slot="status"
            slot-scope="{ row }"
          >
            <i :class="row.statusIcon" />
          </template>
          <template
            slot="actions"
            slot-scope="{ row }"
          >
            <i
              class="fas fa-edit action-icon"
              @click="openEditLoginNoticeForm(row.id)"
            />
          </template>
        </Table>
      </template>
    </div>
  </div>
</template>

<script>
import CustomModal from '../CustomModal'
import AdminToolBar from './AdminToolBar'
import { customModalMixin } from '@/mixins/customModalMixin'
import { SUCCESS, WARNING } from '@/constants/buttons'
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment-timezone'

export default {
  components: {
    CustomModal,
    AdminToolBar
  },

  mixins: [
    customModalMixin
  ],

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      contentWidth: 0,
      contentHeight: 0,
      loading: true,
      list: [],
      activeNotice: {},
      columns: [
        {
          title: 'Description',
          slot: 'description',
          sortable: true
        },
        {
          title: 'Start Date',
          key: 'startDateFormated',
          align: 'center',
          width: '110',
          sortable: true,
          sortType: 'asc'
        },
        {
          title: 'End Date',
          key: 'endDateFormated',
          align: 'center',
          width: '110',
          sortable: true
        },
        {
          title: 'Status',
          slot: 'status',
          align: 'center',
          width: '104',
          sortable: true,
          filters: [
            {
              label: 'Pending',
              value: 'pending'
            },
            {
              label: 'Active',
              value: 'active'
            },
            {
              label: 'Inactive',
              value: 'inactive'
            }
          ],
          filterMultiple: false,
          filterMethod (value, row) {
            return row.status === value
          }
        },
        {
          title: 'Actions',
          slot: 'actions',
          align: 'center',
          width: '60'
        }
      ],
      form: {
        description: '',
        startDate: null,
        endDate: null
      },
      formRules: {
        description: [
          { type: 'string', required: true, message: 'Description cannot be empty', trigger: ['blur', 'change'] }
        ],
        startDate: [
          { type: 'date', required: true, message: 'Start Date cannot be empty', trigger: 'change' }
        ],
        endDate: [
          { type: 'date', required: true, message: 'End Date cannot be empty', trigger: 'change' }
        ]
      },
      filters: {
        status: 'all'
      },
      statusList: [
        'all',
        'pendings',
        'actives',
        'inactives'
      ]
    }
  },

  computed: {
    /**
     * Verifies is the notices form is valid (all required fields have been populated) or not.
     */
    isFormValid () {
      const { description, startDate, endDate } = this.form
      return description.length > 0 && this.isValidDate(startDate) && this.isValidDate(endDate)
    }
  },

  mounted () {
    window.addEventListener('resize', this.getContentMeasures)
    this.getContentMeasures()
    this.fetchLoginNotices()
  },

  methods: {
    /**
     * Gets the width and height of the content according with the browser measures.
     */
    getContentMeasures () {
      if (this.$refs.content) {
        this.contentWidth = this.$refs.content.offsetWidth
        this.contentHeight = this.$refs.content.offsetHeight
      }
    },

    /**
     * Gets the UTC equivalence of a given time.
     * @param {Object} time { hour: 0-24, minute: 0-59, second: 0-59 }
     * @param {String} timeZone
     * @return {String}
     */
    localTimeToUtc (time, timeZone = 'America/Los_Angeles') {
      const currentDate = moment().tz(timeZone)
      const timeDiff = currentDate.diff(time)
      const localTime = new Date(currentDate - timeDiff)
      return this.$moment.utc(localTime).format('HH:mm:ss')
    },

    /**
     * Checks if a provided date is valid or not.
     * @param {Date} date
     * @return {Boolean}
     */
    isValidDate (date) {
      return date instanceof Date && !isNaN(date.valueOf())
    },

    /**
     * Returns an icon class based on the notice status: Pending, Active, Inactive
     * @param {String} status
     * @return {String}
     */
    statusIcon (status) {
      return status === 'inactive'
        ? 'fas fa-ban inactive'
        : (status === 'pending'
          ? 'fas fa-exclamation-triangle hold'
          : 'fas fa-check-circle active'
        )
    },

    /**
     * Fetches the existing Notices.
     */
    async fetchLoginNotices () {
      this.loading = true
      const currentDate = this.$moment(new Date())

      try {
        this.list = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/login-notices`)).data

        this.list.forEach(notice => {
          const startDate = this.$moment(new Date(notice.startDate))
          const endDate = this.$moment(new Date(notice.endDate))
          const isPending = currentDate.isBefore(startDate)
          const isActive = currentDate.isSameOrAfter(startDate) && !currentDate.isAfter(endDate)
          const status = isPending ? 'pending' : (isActive ? 'active' : 'inactive')
          const statusIcon = this.statusIcon(status)
          this.$set(notice, 'startDateFormated', startDate.format('MMM DD, YYYY'))
          this.$set(notice, 'endDateFormated', endDate.format('MMM DD, YYYY'))
          this.$set(notice, 'status', status)
          this.$set(notice, 'statusIcon', statusIcon)
        })
      } catch (error) {
        const content = `Failed to fetch Login Notices: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }

      this.loading = false
    },

    /**
     * Sets up the form by reseting it or populating it.
     * @param {Object} formData
     */
    setLoginNoticeForm (formData = {}) {
      const {
        description = '',
        startDate = null,
        endDate = null
      } = formData

      // Set the form params.
      this.$set(this.form, 'description', description)
      this.$set(this.form, 'startDate', new Date(startDate))
      this.$set(this.form, 'endDate', new Date(endDate))

      if (_.isEmpty(formData)) {
        this.$refs['loginNoticeForm'].resetFields()
        this.$set(this.form, 'active', null)
      }
    },

    /**
     * Opens a modal with a form to add a new Login Notice.
     */
    openAddLoginNoticeForm () {
      this.activeDashboard = {}

      // Initialize the form params.
      this.setLoginNoticeForm()

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Add Notice',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: this.onSaveNotice.bind(this),
            restrictedByFormRules: true
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: true
      }
      this.showModalMixin(modalData)
    },

    /**
     * Opens a form to edit a Notice.
     * @param {Number} noticeId
     */
    openEditLoginNoticeForm (noticeId) {
      if (!noticeId) {
        console.error('No notice id was provided to edit.')
        return
      }

      const activeNotice = this.list.find(notice => notice.id === noticeId)
      this.activeNotice = activeNotice

      // Set the form params.
      this.setLoginNoticeForm(activeNotice)

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Edit Notice',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: this.updateNotice.bind(this),
            restrictedByFormRules: true
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: true
      }
      this.showModalMixin(modalData)
    },

    /**
     * Fetches the data existing into the form for saving/updating purposes.
     * @return {Object}
     */
    fetchFormData () {
      const currentDate = this.$moment(new Date())
      const description = this.form.description
      const startDate = this.$moment(new Date(this.form.startDate))
      const endDate = this.$moment(new Date(this.form.endDate))
      const isPending = currentDate.isBefore(startDate)
      const isActive = currentDate.isSameOrAfter(startDate) && !currentDate.isAfter(endDate)
      const status = isPending ? 'pending' : (isActive ? 'active' : 'inactive')
      const statusIcon = this.statusIcon(status)

      return {
        description,
        startDate,
        endDate,
        status,
        statusIcon
      }
    },

    /**
     * Saves a new Notice.
     */
    async onSaveNotice () {
      const {
        description,
        startDate,
        endDate,
        status,
        statusIcon
      } = this.fetchFormData()

      const newNotice = {
        description: description,
        startDate: `${startDate.format('YYYY-MM-DD')}T00:00:00`,
        endDate: `${endDate.format('YYYY-MM-DD')}T23:59:59`,
        startDateFormated: startDate.format('MMM DD, YYYY'),
        endDateFormated: endDate.format('MMM DD, YYYY'),
        status,
        statusIcon
      }

      const noticeData = {
        description: newNotice.description,
        startDate: newNotice.startDate,
        endDate: newNotice.endDate
      }

      try {
        await axios.post(`${process.env.VUE_APP_API_URI}/admin-console/login-notices`, {
          noticeData
        })

        this.$Message.info({
          content: `Notice ${description} has been added!`,
          duration: 5
        })

        this.list.push(newNotice)
      } catch (error) {
        const content = `Failed to save new Notice: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
      this.closeActionWindow()
    },

    /**
     * Updates a specific Notice.
     */
    async updateNotice () {
      const {
        description,
        startDate,
        endDate,
        status,
        statusIcon
      } = this.fetchFormData()
      const activeNotice = this.activeNotice

      try {
        this.$set(activeNotice, 'description', description)
        this.$set(activeNotice, 'startDate', `${startDate.format('YYYY-MM-DD')}T00:00:00`)
        this.$set(activeNotice, 'endDate', `${endDate.format('YYYY-MM-DD')}T23:59:59`)
        this.$set(activeNotice, 'startDateFormated', startDate.format('MMM DD, YYYY'))
        this.$set(activeNotice, 'endDateFormated', endDate.format('MMM DD, YYYY'))
        this.$set(activeNotice, 'status', status)
        this.$set(activeNotice, 'statusIcon', statusIcon)

        await axios.put(`${process.env.VUE_APP_API_URI}/admin-console/login-notices/${activeNotice.id}`, {
          item: activeNotice
        })

        this.$Message.info({
          content: `${description} has been updated successfully!`,
          duration: 5
        })

        this.closeActionWindow()
      } catch (error) {
        const content = `Failed to update Notice ${description}: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
    },

    /**
     * Displays a confirm window for reset the content of the Login Notices.
     */
    onResetNotices () {
      const modalData = {
        title: 'Confirm Reset Data',
        category: 'warning',
        message: 'Do you confirm to reset the Login Notices?',
        actionButtons: [
          {
            label: 'Confirm',
            type: WARNING,
            callback: this.resetNotices.bind(this)
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: false
      }
      this.showModalMixin(modalData)
    },

    /**
     * Resets the content of the Login Notices.
     */
    async resetNotices () {
      try {
        await axios.delete(`${process.env.VUE_APP_API_URI}/admin-console/login-notices`)

        this.list = []

        this.$Message.info({
          content: `Login Notices has been reset successfully!`,
          duration: 5
        })
      } catch (error) {
        const content = `Failed to reset Login Notices: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }

      this.closeActionWindow()
    },

    /**
     * Closes the action window (modal).
     */
    closeActionWindow () {
      this.hideModalMixin()
    }
  }
}
</script>

<style scoped>
  .main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .spin,
  .table >>> .ivu-table-cell .spin {
    animation: ani-demo-spin 1s linear infinite;
  }

  .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .row .element-row,
  .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .row .element-row p {
    margin: 0;
  }

  .row .select-input {
    width: 100%;
  }

  .form >>> .ivu-form-item-content {
    display: flex;
    flex-direction: column;
  }

  .two-columns {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .two-columns .column-left {
    margin-left: 0;
    margin-right: 4px;
    width: calc(50% - 8px)
  }

  .two-columns .column-right {
    margin-left: 4px;
    margin-right: 0;
    width: calc(50% - 4px)
  }

  .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .title .actions {
    width: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .content {
    width: 100%;
    height: calc(100% - 38px);
    overflow: auto;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .no-data {
    font-size: 24px;
    color: var(--color-gray);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-icon {
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-black);
    cursor: pointer;
  }

  .table >>> .ivu-table-cell {
    padding: 0 8px;
  }

  .table >>> .ivu-table-cell .fas {
    font-size: 16px;
  }

  .table >>> .ivu-table-cell .action-icon {
    cursor: pointer;
  }

  .table >>> .ivu-table-cell .action-icon:not(:first-child) {
    margin-left: 8px;
  }

  .table >>> .ivu-table-cell .action-icon.hidden {
    display: none;
  }

  .active {
    color: var(--color-success);
  }

  .hold {
    color: var(--color-yellow-dark);
  }

  .inactive {
    color: var(--color-red-border);
  }
</style>
